// src/constants/exploreFilters.js
// Filter options for the Explore mode on Landing Page

export const SPORTS_OPTIONS = [
  { value: 'All', label: 'All Sports' },
  { value: 'Soccer', label: 'Soccer' },
  { value: 'Basketball', label: 'Basketball' },
  { value: 'Baseball', label: 'Baseball' },
  { value: 'Football', label: 'Football' },
  { value: 'Tennis', label: 'Tennis' },
  { value: 'Volleyball', label: 'Volleyball' },
  { value: 'Lacrosse', label: 'Lacrosse' },
  { value: 'Hockey', label: 'Hockey' },
  { value: 'Swimming', label: 'Swimming' },
  { value: 'Golf', label: 'Golf' },
  { value: 'Track', label: 'Track & Field' },
  { value: 'Gymnastics', label: 'Gymnastics' },
  { value: 'Martial Arts', label: 'Martial Arts' },
  { value: 'Wrestling', label: 'Wrestling' },
  { value: 'Cricket', label: 'Cricket' },
  { value: 'Rugby', label: 'Rugby' },
  { value: 'Badminton', label: 'Badminton' },
  { value: 'Table Tennis', label: 'Table Tennis' },
  { value: 'Other', label: 'Other' }
];

export const AGE_GROUP_OPTIONS = [
  { value: 'all', label: 'All Ages', minAge: 0, maxAge: 100 },
  { value: 'toddler', label: 'Toddler (2-4)', minAge: 2, maxAge: 4 },
  { value: 'u6', label: 'U6 (5-6)', minAge: 5, maxAge: 6 },
  { value: 'u8', label: 'U8 (7-8)', minAge: 7, maxAge: 8 },
  { value: 'u10', label: 'U10 (9-10)', minAge: 9, maxAge: 10 },
  { value: 'u12', label: 'U12 (11-12)', minAge: 11, maxAge: 12 },
  { value: 'u14', label: 'U14 (13-14)', minAge: 13, maxAge: 14 },
  { value: 'u16', label: 'U16 (15-16)', minAge: 15, maxAge: 16 },
  { value: 'u18', label: 'U18 (17-18)', minAge: 17, maxAge: 18 },
  { value: 'adult', label: 'Adult (18+)', minAge: 18, maxAge: 65 },
  { value: 'senior', label: 'Senior (50+)', minAge: 50, maxAge: 100 }
];

export const PRICE_RANGE_OPTIONS = [
  { value: 'all', label: 'Any Price', min: 0, max: Infinity },
  { value: 'free', label: 'Free', min: 0, max: 0 },
  { value: 'under50', label: 'Under $50', min: 0, max: 50 },
  { value: '50to100', label: '$50 - $100', min: 50, max: 100 },
  { value: '100to200', label: '$100 - $200', min: 100, max: 200 },
  { value: '200to500', label: '$200 - $500', min: 200, max: 500 },
  { value: 'over500', label: '$500+', min: 500, max: Infinity }
];

export const START_DATE_OPTIONS = [
  { value: 'all', label: 'Any Time' },
  { value: 'thisMonth', label: 'This Month' },
  { value: 'nextMonth', label: 'Next Month' },
  { value: 'next3Months', label: 'Next 3 Months' },
  { value: 'spring', label: 'Spring 2026' },
  { value: 'summer', label: 'Summer 2026' },
  { value: 'fall', label: 'Fall 2026' }
];

// Helper function to get date range for start date filter
export const getStartDateRange = (filterValue) => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();

  switch (filterValue) {
    case 'thisMonth': {
      const start = new Date(currentYear, currentMonth, 1);
      const end = new Date(currentYear, currentMonth + 1, 0);
      return { start, end };
    }
    case 'nextMonth': {
      const start = new Date(currentYear, currentMonth + 1, 1);
      const end = new Date(currentYear, currentMonth + 2, 0);
      return { start, end };
    }
    case 'next3Months': {
      const start = now;
      const end = new Date(currentYear, currentMonth + 3, now.getDate());
      return { start, end };
    }
    case 'spring': {
      // Spring: March - May
      const year = currentMonth > 4 ? currentYear + 1 : currentYear;
      return { start: new Date(year, 2, 1), end: new Date(year, 5, 0) };
    }
    case 'summer': {
      // Summer: June - August
      const year = currentMonth > 7 ? currentYear + 1 : currentYear;
      return { start: new Date(year, 5, 1), end: new Date(year, 8, 0) };
    }
    case 'fall': {
      // Fall: September - November
      const year = currentMonth > 10 ? currentYear + 1 : currentYear;
      return { start: new Date(year, 8, 1), end: new Date(year, 11, 0) };
    }
    default:
      return { start: null, end: null };
  }
};

// Default filter state
export const DEFAULT_EXPLORE_FILTERS = {
  sport: 'All',
  ageGroup: 'all',
  priceRange: 'all',
  startDate: 'all',
  searchQuery: '',
  hasOpenings: false
};

// Availability status helpers
export const AVAILABILITY_STATUS = {
  AVAILABLE: 'available',     // 6+ spots
  LIMITED: 'limited',         // 1-5 spots
  WAITLIST: 'waitlist',       // Full but waitlist available
  CLOSED: 'closed'            // No enrollment
};

export const getAvailabilityStatus = (program, capacityStatus) => {
  if (!program?.capacity || !capacityStatus) {
    return { status: AVAILABILITY_STATUS.AVAILABLE, spotsLeft: null };
  }

  const { availableSpots, isAtCapacity, waitlistEnabled } = capacityStatus;

  if (!isAtCapacity && availableSpots > 5) {
    return { status: AVAILABILITY_STATUS.AVAILABLE, spotsLeft: availableSpots };
  }
  
  if (!isAtCapacity && availableSpots > 0) {
    return { status: AVAILABILITY_STATUS.LIMITED, spotsLeft: availableSpots };
  }
  
  if (isAtCapacity && waitlistEnabled) {
    return { status: AVAILABILITY_STATUS.WAITLIST, spotsLeft: 0 };
  }
  
  return { status: AVAILABILITY_STATUS.CLOSED, spotsLeft: 0 };
};

// Get the primary cost from a program's cost structure
export const getProgramPrimaryCost = (program) => {
  if (!program?.cost) return null;

  // Check registration fees first
  const regFees = program.cost.registrationFees;
  if (regFees && regFees.length > 0 && regFees[0].amount) {
    return {
      amount: parseFloat(regFees[0].amount) || 0,
      type: 'registration',
      label: 'Registration'
    };
  }

  // Check fee structure
  const feeStructure = program.cost.feeStructure;
  if (feeStructure && feeStructure.length > 0 && feeStructure[0].amount) {
    return {
      amount: parseFloat(feeStructure[0].amount) || 0,
      type: 'recurring',
      interval: feeStructure[0].interval || 'Monthly',
      label: feeStructure[0].interval || 'Monthly'
    };
  }

  return { amount: 0, type: 'free', label: 'Free' };
};

// Format cost for display
export const formatProgramCost = (program) => {
  const cost = getProgramPrimaryCost(program);
  
  if (!cost || cost.amount === 0) {
    return 'Free';
  }

  const formattedAmount = `$${cost.amount.toLocaleString()}`;
  
  if (cost.type === 'recurring') {
    const intervalLabel = cost.interval === 'Monthly' ? '/mo' : 
                          cost.interval === 'Weekly' ? '/wk' : 
                          cost.interval === 'Yearly' ? '/yr' : '';
    return `${formattedAmount}${intervalLabel}`;
  }

  return formattedAmount;
};

// Get program age range display
export const formatProgramAgeRange = (program) => {
  if (!program?.age) return 'All Ages';
  
  const { min, max } = program.age;
  
  if (!min && !max) return 'All Ages';
  if (min && !max) return `${min}+`;
  if (!min && max) return `Up to ${max}`;
  
  return `Ages ${min}-${max}`;
};

// Format program start date
export const formatProgramStartDate = (program) => {
  if (!program?.schedule?.startDate) return 'TBD';
  
  const startDate = new Date(program.schedule.startDate);
  const now = new Date();
  
  // Check if it's in the past
  if (startDate < now) {
    return 'In Progress';
  }
  
  return startDate.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: startDate.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
  });
};










